import { isTableLine } from 'shared/markdown/utils'
import { type CopilotTableData } from './types'

const ALIGNMENT_ROW_PATTERN = /^\|?(\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?$/

export const slugifyTableColumnKey = (header: string, index: number): string => {
  const slug = header
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '_')
    .replaceAll(/^_+|_+$/g, '')

  return slug.length > 0 ? slug : `column_${index + 1}`
}

const parseTableRow = (line: string): string[] => {
  const trimmed = line.trim()
  if (!trimmed.startsWith('|')) {
    return []
  }

  const inner = trimmed.endsWith('|') ? trimmed.slice(1, -1) : trimmed.slice(1)
  return inner.split('|').map((cell) => cell.trim())
}

const isAlignmentRow = (line: string): boolean => ALIGNMENT_ROW_PATTERN.test(line.trim())

export const parseMarkdownTableBlock = (lines: string[]): Omit<CopilotTableData, 'sourceTableId'> | null => {
  if (lines.length < 2) {
    return null
  }

  const headers = parseTableRow(lines[0])
  if (headers.length < 2) {
    return null
  }

  const dataStartIndex = isAlignmentRow(lines[1]) ? 2 : 1
  const rows: string[][] = []

  for (let index = dataStartIndex; index < lines.length; index++) {
    const cells = parseTableRow(lines[index])
    if (cells.length === 0) {
      continue
    }

    if (cells.length !== headers.length) {
      return null
    }

    rows.push(cells)
  }

  if (rows.length === 0) {
    return null
  }

  return { headers, rows }
}

export const COPILOT_TABLE_TOKEN_TAG = 'copilot-table'

export const replaceCopilotTableTokens = (markdown: string): string =>
  markdown.replaceAll(
    new RegExp(`<${COPILOT_TABLE_TOKEN_TAG}\\s+data-id="([^"]+)"\\s*></${COPILOT_TABLE_TOKEN_TAG}>`, 'g'),
    (_, id) => `<${COPILOT_TABLE_TOKEN_TAG} data-id="${id}"></${COPILOT_TABLE_TOKEN_TAG}>`
  )

export const extractMarkdownTables = (
  markdown: string
): {
  markdown: string
  tableIdMap: Map<string, CopilotTableData>
} => {
  const lines = markdown.split('\n')
  const tableIdMap = new Map<string, CopilotTableData>()
  const output: string[] = []
  let tableIndex = 0

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index]

    if (!isTableLine(line)) {
      output.push(line)
      continue
    }

    const block: string[] = [line]
    let cursor = index + 1

    while (cursor < lines.length && isTableLine(lines[cursor])) {
      block.push(lines[cursor])
      cursor++
    }

    const parsed = parseMarkdownTableBlock(block)
    if (parsed == null) {
      output.push(...block)
    } else {
      const sourceTableId = `copilot-table-${tableIndex}`
      tableIndex++
      tableIdMap.set(sourceTableId, {
        sourceTableId,
        ...parsed
      })
      output.push(`<${COPILOT_TABLE_TOKEN_TAG} data-id="${sourceTableId}"></${COPILOT_TABLE_TOKEN_TAG}>`)
    }

    index = cursor - 1
  }

  return {
    markdown: output.join('\n'),
    tableIdMap
  }
}
